// Kanemori Sanctuary countryside (M10): live, client-only touches drawn over the painted 2x scenes: the wildlife
// (simulated in country-wild.ts), sakura petals drifting off the crowns along the road, and steam over the onsen.
// Cosmetic only: no collision, no server state. Bounded by city-art.json countryFx (validated); petal and steam counts
// and brightness are scaled by the comfort grade (Full, Soft, Low and the Low effects setting).
import { Container, Graphics } from 'pixi.js';
import { CITY_ART, WORLD } from '../../common/src/data';
import { TILE, type GameMap } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import { PALETTE } from './theme';
import { mixHex } from './maplayout';
import { FxPool } from './fx-pool';
import { CountryWild, type Critter } from './country-wild';
import { countryPlan } from '../../common/src/country-plan';

const CF = CITY_ART.countryFx;
const n = (s: string) => parseInt(s.slice(1), 16);
const { midnight: NIGHT, slate: SLATE, offWhite: WHITE, cyan: CYAN, pink: PINK, amber: AMBER } = PALETTE;
const C = {
  ink: n(mixHex(NIGHT, '#000000', 0.35)), deer: n(mixHex(mixHex(AMBER, SLATE, 0.55), NIGHT, 0.25)), deerHi: n(mixHex(AMBER, WHITE, 0.25)),
  tanuki: n(mixHex(SLATE, AMBER, 0.25)), mask: n(mixHex(NIGHT, SLATE, 0.3)), crow: n(mixHex(NIGHT, SLATE, 0.2)), crowHi: n(SLATE),
  heron: n(mixHex(WHITE, SLATE, 0.25)), beak: n(AMBER), white: n(WHITE), koi: n(mixHex(AMBER, PINK, 0.35)), koiW: n(WHITE),
  petal: n(mixHex(PINK, WHITE, 0.55)), petalHi: n(mixHex(PINK, WHITE, 0.75)), fly: n(mixHex(AMBER, CYAN, 0.3)), wing: [n(mixHex(PINK, WHITE, 0.4)), n(AMBER), n(WHITE)],
  shadow: n(NIGHT), water: n(mixHex(CYAN, WHITE, 0.3)),
};
type Petal = { x: number; y: number; vx: number; t0: number; fall: number; ground: number };
type Wisp = { x: number; y: number; t0: number; r: number };

export class CountryFx {
  /** on the ground, under the people: walking and grazing animals, koi, landed petals, bird shadows */
  ground = new Graphics();
  /** over the people: birds in the air, butterflies, fireflies, falling petals, steam */
  over = new Graphics();
  readonly wild = new CountryWild();
  private petals = new FxPool<Petal>(CF.petals);
  private steam = new FxPool<Wisp>(CF.steam);
  private sakura: { x: number; y: number }[];
  private pool: { x: number; y: number } | null;
  private petalDue = 0; private steamDue = 0;
  constructor(groundLayer: Container, overLayer: Container, map: GameMap) {
    groundLayer.addChild(this.ground); overLayer.addChild(this.over);
    this.sakura = countryPlan(map).trees.filter(t => t.kind === 'sakura');
    const p = WORLD.healZones.find(z => z.name === 'Sanctuary onsen');
    this.pool = p ? { x: (p.x + p.w / 2) * TILE, y: (p.y + p.h / 2) * TILE } : null;
    this.disc = p?.circle ? { x: p.circle.cx * TILE, y: p.circle.cy * TILE, r: p.circle.r * TILE } : null;
  }
  private disc: { x: number; y: number; r: number } | null;

  /** Is a person standing in the hot spring (the heal circle sized to the pool)? Derived from position, so every client agrees.
   *  render.ts cuts such a person off at the waterline; the ripple below is drawn at the same line. */
  wading(x: number, y: number) { const d = this.disc; return !!d && Math.hypot(x - d.x, y - d.y) < d.r; }
  /** world px above the feet where the water line cuts the body (the waist) */
  static readonly WATERLINE = 10;

  draw(t: number, dt: number, cam: { x: number; y: number }, players: { x: number; y: number; v?: unknown }[], comfort: number, view: { x0: number; x1: number; y0: number; y1: number }) {
    const g = this.ground, o = this.over; g.clear(); o.clear();
    const strip = WORLD.districts.find(d => d.id === 'sanctuary');
    if (!strip || view.x1 < strip.x * TILE) return; // nothing out there to animate from the city
    dt = Math.min(dt, 0.1);
    this.wild.step(dt, t, cam, players);
    const vis = (x: number, y: number, m = 24) => x > view.x0 - m && x < view.x1 + m && y > view.y0 - m && y < view.y1 + m;
    for (const c of this.wild.list) if (vis(c.x, c.y)) this.critter(c, t);
    // sakura petals: a few at a time from the crowns in view, drifting down and settling, then fading
    const near = this.sakura.filter(s => vis(s.x, s.y, 40));
    if (near.length) {
      this.petalDue += dt * 7 * comfort;
      for (; this.petalDue >= 1; this.petalDue--) {
        const s = near[Math.floor(Math.random() * near.length)];
        this.petals.put({ x: s.x + (Math.random() - 0.5) * 30, y: s.y - 30 - Math.random() * 10, vx: 4 + Math.random() * 5, t0: t, fall: 7 + Math.random() * 5, ground: s.y - 8 + Math.random() * 22 });
      }
    }
    for (const p of this.petals.items) {
      const age = t - p.t0, life = CF.petalSec; if (age < 0 || age > life) continue;
      const fallen = Math.min(p.ground - p.y, age * p.fall), x = p.x + age * p.vx + Math.sin(age * 2.6 + p.x) * 4, y = p.y + fallen;
      if (!vis(x, y)) continue;
      const a = Math.min(1, (life - age) / 1.2) * (0.55 + 0.45 * comfort), onGround = fallen >= p.ground - p.y;
      (onGround ? g : o).rect(x, y, Math.sin(age * 9 + p.x) > 0 ? 1 : 0.5, 0.5).fill({ color: onGround ? C.petal : C.petalHi, alpha: a });
    }
    // bathers: a water skin and two ripples round the waist of everyone standing in the hot spring (their legs are cut off in render.ts)
    for (const p of players) {
      if (p.v || !this.wading(p.x, p.y) || !vis(p.x, p.y)) continue;
      const wy = p.y + 5 - CountryFx.WATERLINE;
      o.ellipse(p.x, wy, 8.5, 3).fill({ color: C.water, alpha: 0.55 });
      for (let k = 0; k < 2; k++) {
        const ph = (t * 0.7 + k * 0.5 + p.x * 0.01) % 1;
        o.ellipse(p.x, wy, 7 + ph * 9, 2.6 + ph * 3.4).stroke({ color: C.white, width: 1, alpha: (1 - ph) * (0.35 + 0.35 * comfort) });
      }
    }
    // onsen steam: a few thin wisps lifting off the water near the rim and thinning out; never a cloud, so a bather
    // waist-deep in the pool stays plainly visible (wisps over a bather fade further)
    if (this.pool && vis(this.pool.x, this.pool.y, 80)) {
      this.steamDue += dt * 3.2;
      for (; this.steamDue >= 1; this.steamDue--) {
        const a = Math.random() * Math.PI * 2, k = 0.62 + Math.random() * 0.5; // a ring round the rim, not the middle
        this.steam.put({ x: this.pool.x + Math.cos(a) * 34 * k, y: this.pool.y + Math.sin(a) * 26 * k, t0: t, r: 3 + Math.random() * 2.5 });
      }
      const bathers = players.filter(p => !p.v && this.wading(p.x, p.y));
      for (const w of this.steam.items) {
        const age = t - w.t0; if (age < 0 || age > 4.2) continue;
        const fade = (1 - age / 4.2) * Math.min(1, age * 1.5); // soft in, soft out
        const x = w.x + Math.sin(age * 1.1 + w.x) * 3, y = w.y - age * 6;
        let a = fade * 0.5 * (0.5 + 0.5 * comfort);
        for (const b of bathers) if (Math.abs(x - b.x) < 16 && y > b.y - 24 && y < b.y + 8) a *= 0.25;
        o.ellipse(x, y, w.r * 2.5 + age * 1.8, w.r * 0.8 + age * 0.6).fill({ color: C.white, alpha: a });
      }
    }
  }

  private critter(c: Critter, t: number) {
    const g = this.ground, o = this.over, f = c.face, x = c.x, y = c.y, a = c.fade;
    if (a <= 0.02) return;
    const R = (gr: Graphics, rx: number, ry: number, w: number, h: number, color: number, al = 1) => gr.rect(f > 0 ? x + rx : x - rx - w, y + ry, w, h).fill({ color, alpha: al * a });
    switch (c.kind) {
      case 'deer': {
        const graze = c.state === 0 && Math.sin(t * 0.7 + c.seed) > 0.2, run = c.state === 2, step = run ? Math.sin(t * 16) : c.state === 1 ? Math.sin(t * 6) : 0;
        g.ellipse(x, y, 6, 1.5).fill({ color: C.shadow, alpha: 0.35 * a });
        for (const lx of [-3.5, -2.5, 2, 3]) R(g, lx, -4 + (run ? Math.abs(step) * -1.5 : 0), 0.5, 4 - (run ? 0 : Math.abs(step) * 0.5), C.ink);
        R(g, -4.5, -8.5, 9, 4.5, C.ink); R(g, -4, -8, 8, 3.5, C.deer);
        for (const [sx, sy] of [[-2.5, -7.5], [-0.5, -7], [1.5, -7.5]]) R(g, sx, sy, 0.5, 0.5, C.deerHi);
        R(g, -5, -8.5, 1, 1.5, C.white); // tail
        if (graze) { R(g, 3.5, -6.5, 1.5, 3, C.ink); R(g, 4, -4, 2.5, 1.5, C.deer); }
        else { R(g, 3, -11.5, 1.5, 4, C.deer); R(g, 3.5, -13, 3, 2, C.deer); R(g, 6, -12.5, 0.5, 0.5, C.ink); R(g, 3.5, -14, 0.5, 1, C.ink); }
        break;
      }
      case 'tanuki': {
        const step = c.state ? Math.sin(t * 18) * 0.5 : 0;
        g.ellipse(x, y, 4, 1).fill({ color: C.shadow, alpha: 0.35 * a });
        R(g, -3, -3.5 + step, 6, 3, C.ink); R(g, -2.5, -3 + step, 5, 2, C.tanuki); R(g, 2, -4.5 + step, 2.5, 2.5, C.tanuki); R(g, 2.5, -4 + step, 1.5, 1, C.mask);
        R(g, -4.5, -3 + step, 2, 1.5, C.tanuki); R(g, -4, -3 + step, 0.5, 1.5, C.mask);
        break;
      }
      case 'crow': {
        if (c.z > 0.5) { // in the air: a shadow on the ground, flapping wings above
          g.ellipse(x, y, 2.5, 0.8).fill({ color: C.shadow, alpha: 0.3 * a });
          const flap = Math.sin(t * 22 + c.seed) * 2.5;
          o.rect(x - 1.5, y - c.z - 1, 3, 1.5).fill({ color: C.crow, alpha: a });
          o.rect(x - 4, y - c.z - 1 - flap, 2.5, 1).fill({ color: C.crow, alpha: a }); o.rect(x + 1.5, y - c.z - 1 - flap, 2.5, 1).fill({ color: C.crow, alpha: a });
        } else {
          const peck = c.state === 0 && Math.sin(t * 4 + c.seed) > 0.6 ? 0.5 : 0;
          g.ellipse(x, y, 2, 0.6).fill({ color: C.shadow, alpha: 0.3 * a });
          R(g, -1.5, -3, 3, 2, C.crow); R(g, -1, -3, 1.5, 0.5, C.crowHi); R(g, 1.5, -3.5 + peck, 1.5, 1.5, C.crow); R(g, 3, -3 + peck, 0.5, 0.5, C.ink); R(g, -2.5, -2.5, 1, 1, C.crow);
          R(g, -0.5, -1, 0.5, 1, C.ink); R(g, 0.5, -1, 0.5, 1, C.ink);
        }
        break;
      }
      case 'heron': {
        if (c.z > 0.5) {
          g.ellipse(x, y, 4, 1).fill({ color: C.shadow, alpha: 0.25 * a });
          const flap = Math.sin(t * 7 + c.seed) * 3, yy = y - c.z;
          o.rect(x - 2, yy - 1, 4, 2).fill({ color: C.heron, alpha: a }); o.rect(x - 7, yy - 1 - flap, 5, 1).fill({ color: C.heron, alpha: a }); o.rect(x + 2, yy - 1 - flap, 5, 1).fill({ color: C.heron, alpha: a });
          o.rect(f > 0 ? x + 2 : x - 4, yy - 1.5, 2, 1).fill({ color: C.heron, alpha: a }); o.rect(f > 0 ? x + 4 : x - 5, yy - 1.5, 1, 0.5).fill({ color: C.beak, alpha: a });
        } else {
          g.ellipse(x, y, 2.5, 0.7).fill({ color: C.shadow, alpha: 0.3 * a });
          R(g, -0.5, -5, 0.5, 5, C.ink); R(g, 0.5, -5, 0.5, 5, C.ink);
          R(g, -2.5, -9, 4, 4, C.heron); R(g, -2.5, -9, 4, 0.5, C.white); R(g, 1, -13, 1, 4.5, C.heron); R(g, 1.5, -14, 1.5, 1.5, C.heron); R(g, 3, -13.5, 2, 0.5, C.beak); R(g, -3, -8, 1, 2, C.crowHi);
        }
        break;
      }
      case 'koi': {
        const sw = Math.sin(t * 6 + c.seed) * 0.5, spots = hash2(c.seed, 2) < 0.5;
        g.ellipse(x, y, 2.2, 0.9).fill({ color: spots ? C.koiW : C.koi, alpha: 0.85 * a });
        g.rect(f > 0 ? x - 3.5 : x + 2, y - 0.5 + sw, 1.5, 1).fill({ color: C.koi, alpha: 0.85 * a });
        if (spots) g.rect(x - 0.5, y - 0.5, 1.2, 0.8).fill({ color: C.koi, alpha: 0.9 * a });
        break;
      }
      case 'butterfly': {
        const flap = Math.abs(Math.sin(t * 14 + c.seed)) * 1.5, col = C.wing[c.seed % 3], yy = y - c.z;
        g.ellipse(x, y, 1, 0.4).fill({ color: C.shadow, alpha: 0.2 });
        o.rect(x - 0.5 - flap, yy - 1, flap + 0.5, 1.5).fill({ color: col, alpha: 0.9 }); o.rect(x + 0.5, yy - 1, flap + 0.5, 1.5).fill({ color: col, alpha: 0.9 });
        o.rect(x - 0.25, yy - 1, 0.5, 1.5).fill({ color: C.ink, alpha: 0.9 });
        break;
      }
      case 'firefly': {
        const blink = Math.max(0, Math.sin(t * 1.7 + c.seed * 1.3)), yy = y - c.z; if (blink < 0.05) break;
        o.circle(x, yy, 3).fill({ color: C.fly, alpha: 0.12 * blink }); o.rect(x - 0.25, yy - 0.25, 0.5, 0.5).fill({ color: C.fly, alpha: blink });
        break;
      }
    }
  }
}
